/* Motivband im Abschlussabschnitt (sections/fs-cta.liquid, 2026-09-30).
   Zwei Ebenen, beide per transform (Grafikkarte, stufenlos):
   - belt: gleichmäßiges Laufen als endlose Web-Animation (0 → eine Reihenbreite, linear).
   - reel: eigene Verschiebung durch Pfeile, Trackpad und Finger.
   Die erste Fassung bewegte scrollLeft; das springt nur in halben Pixeln (gemessen) und stotterte
   bei 20 px/s sichtbar. Drei gleiche Reihen halten das Band lückenlos: die Gesamtverschiebung
   (belt + reel) wird unsichtbar um ganze Reihenbreiten versetzt, damit rechts und links immer
   Band da ist.
   Pause beim Überfahren, bei Tastaturfokus, beim Bedienen (2,5 s), außerhalb des Sichtfelds und
   bei „Bewegung reduzieren"; dauerhaft über den Knopf Anhalten/Fortsetzen (WCAG 2.2.2).
   Ohne Skript bleibt die erste Reihe sichtbar und wischbar. */
(() => {
  if (customElements.get('fs-motif-band')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const RESUME_AFTER = 2500; // ms Ruhe nach eigener Bedienung
  const ITEMS_PER_STEP = 3;
  const PAUSE_KEY = 'fs-motif-band-paused'; // vom Besucher angehalten – gilt auch auf den nächsten Seiten

  class FsMotifBand extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.viewport = this.querySelector('.fs-cta__viewport');
      this.reel = this.querySelector('.fs-cta__reel');
      this.belt = this.querySelector('.fs-cta__belt');
      this.track = this.querySelector('.fs-cta__track');
      this.prev = this.querySelector('.fs-cta__arrow--prev');
      this.next = this.querySelector('.fs-cta__arrow--next');
      this.pauseButton = this.querySelector('.fs-cta__pause');
      if (!this.viewport || !this.reel || !this.belt || !this.track) return;
      this.bound = true;

      this.speed = Number(this.dataset.speed) || 0; // Pixel pro Sekunde
      this.offset = 0; // eigene Verschiebung in px (positiv = Band nach links)
      this.hovered = false;
      this.focused = false;
      this.dragging = false;
      this.visible = true;
      this.holdUntil = 0;
      this.userPaused = false;
      try {
        this.userPaused = window.localStorage.getItem(PAUSE_KEY) === '1';
      } catch (error) {
        // Speicher gesperrt – dann eben nur für diesen Seitenaufruf
      }

      // Anhalten/Fortsetzen (Barrierefreiheit): bleibt angehalten, bis der Besucher wieder startet
      this.pauseButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        try {
          if (this.userPaused) window.localStorage.setItem(PAUSE_KEY, '1');
          else window.localStorage.removeItem(PAUSE_KEY);
        } catch (error) {
          // Speicher gesperrt
        }
        if (!this.userPaused) this.holdUntil = 0;
        this.syncPauseButton();
        this.update();
      });

      this.prev?.addEventListener('click', () => this.step(-1));
      this.next?.addEventListener('click', () => this.step(1));

      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.update();
      });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = false;
        this.update();
      });
      // Nur Tastaturfokus hält an – ein Mausklick auf einen Pfeil fokussiert ihn auch,
      // danach soll das Band nach der Ruhezeit weiterlaufen
      this.addEventListener('focusin', (event) => {
        this.focused = event.target.matches(':focus-visible') && !event.target.closest('.fs-cta__pause');
        this.update();
      });
      this.addEventListener('focusout', () => {
        this.focused = false;
        this.update();
      });

      // Tastatur: der Browser scrollt ein fokussiertes Motiv ins Bild (auch bei overflow: hidden) –
      // das in die eigene Verschiebung übernehmen
      this.viewport.addEventListener('scroll', () => {
        const x = this.viewport.scrollLeft;
        if (!x) return;
        this.viewport.scrollLeft = 0;
        this.place(this.offset + x);
      });

      // Trackpad: waagrecht wischen verschiebt das Band, senkrecht scrollt weiter die Seite
      this.viewport.addEventListener(
        'wheel',
        (event) => {
          if (this.static || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
          event.preventDefault();
          const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.viewport.clientWidth : 1;
          this.hold();
          this.settle();
          this.place(this.offset + event.deltaX * unit);
        },
        { passive: false }
      );

      // Finger/Stift: ziehen, beim Loslassen mit Schwung ausrollen
      this.viewport.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.viewport.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.viewport.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.viewport.addEventListener('pointercancel', this.onPointerUp.bind(this));
      // Nach dem Ziehen keinen Klick auf das Motiv auslösen
      this.viewport.addEventListener(
        'click',
        (event) => {
          if (!this.suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
        },
        true
      );

      this.reel.addEventListener('transitionend', (event) => {
        if (event.target !== this.reel) return;
        this.reel.classList.remove('is-gliding', 'is-flinging');
        this.place(this.offset); // nach dem Gleiten unsichtbar zurück ins Grundmaß
      });

      if ('IntersectionObserver' in window) {
        this.visible = false;
        let reported = false;
        this.observer = new IntersectionObserver((entries) => {
          reported = true;
          this.visible = entries[0].isIntersecting;
          this.update();
        });
        this.observer.observe(this);
        // Manche Vorschau-Renderer melden nie – dann als sichtbar annehmen
        setTimeout(() => {
          if (!reported) {
            this.visible = true;
            this.update();
          }
        }, 1500);
      }

      reduceMotion.addEventListener?.('change', () => {
        this.syncPauseButton();
        this.update();
      });
      this.onResize = () => this.measure();
      window.addEventListener('resize', this.onResize);
      this.measure();
    }

    disconnectedCallback() {
      this.bound = false;
      this.observer?.disconnect();
      this.auto?.cancel();
      clearTimeout(this.resumeTimer);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }

    measure() {
      this.classList.remove('is-static');
      const copy = this.track.nextElementSibling;
      this.half = copy ? copy.offsetLeft - this.track.offsetLeft : 0; // eine Reihe inkl. Abstand
      this.static = !(this.half > this.viewport.clientWidth + 1); // passt ganz hinein → kein Karussell
      this.classList.toggle('is-static', this.static);
      [this.prev, this.next].forEach((button) => button && (button.hidden = this.static));

      const progress = this.auto ? this.beltOffset() / this.autoHalf : 0;
      this.auto?.cancel();
      this.auto = null;
      if (this.static) {
        this.render(0, null);
        this.syncPauseButton();
        return;
      }

      if (this.speed > 0) {
        const duration = (this.half / this.speed) * 1000;
        this.auto = this.belt.animate(
          [{ transform: 'translate3d(0, 0, 0)' }, { transform: `translate3d(${-this.half}px, 0, 0)` }],
          { duration, iterations: Infinity, easing: 'linear' }
        );
        this.autoHalf = this.half;
        this.auto.pause();
        this.auto.currentTime = progress * duration;
      }
      this.place(this.offset);
      this.syncPauseButton();
      this.update();
    }

    // Knopf nur zeigen, wenn sich überhaupt etwas von selbst bewegt
    syncPauseButton() {
      const button = this.pauseButton;
      if (!button) return;
      button.hidden = this.static || !this.auto || reduceMotion.matches;
      button.classList.toggle('is-paused', this.userPaused);
      button.setAttribute('aria-label', this.userPaused ? 'Laufband fortsetzen' : 'Laufband anhalten');
    }

    // Aktuelle Laufstrecke des belt in px (0 bis eine Reihenbreite)
    beltOffset() {
      if (!this.auto) return 0;
      const duration = this.auto.effect.getTiming().duration;
      return (((this.auto.currentTime || 0) % duration) / duration) * this.autoHalf;
    }

    render(value, mode) {
      const animate = mode && !reduceMotion.matches;
      this.offset = value;
      this.reel.classList.toggle('is-gliding', animate && mode === 'glide');
      this.reel.classList.toggle('is-flinging', animate && mode === 'fling');
      this.reel.style.transform = `translate3d(${-value}px, 0, 0)`;
      if (mode && !animate) this.place(value);
    }

    // Sofort setzen und die Gesamtverschiebung ins Grundmaß [0, Reihenbreite) legen –
    // so bleibt rechts Platz, auch wenn das Band danach eine ganze Reihe weiterläuft
    place(value) {
      if (!(this.half > 0)) return;
      const belt = this.beltOffset();
      const total = (((value + belt) % this.half) + this.half) % this.half;
      this.render(total - belt, null);
    }

    // Um eine Strecke gleiten; vorher unsichtbar so versetzen, dass Band in Fahrtrichtung da ist
    glideBy(distance, mode) {
      const belt = this.beltOffset();
      let start = this.offset;
      if (start + belt + distance < 0) start += this.half;
      if (start + belt + distance + this.viewport.clientWidth > 3 * this.half) start -= this.half;
      this.render(start, null);
      void this.reel.offsetWidth; // Startlage festschreiben, sonst gleitet der Versatz mit
      this.render(start + distance, mode);
    }

    // Ein laufendes Gleiten sofort an seinem Ziel beenden, damit der nächste Schritt dort ansetzt
    settle() {
      if (!this.reel.classList.contains('is-gliding') && !this.reel.classList.contains('is-flinging')) return;
      this.reel.classList.remove('is-gliding', 'is-flinging');
      this.place(this.offset);
    }

    step(direction) {
      if (this.static) return;
      this.hold();
      this.settle();
      const item = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const width = item ? item.getBoundingClientRect().width + gap : 200;
      this.glideBy(direction * width * ITEMS_PER_STEP, 'glide');
    }

    onPointerDown(event) {
      if (this.static || event.pointerType === 'mouse') return;
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false, samples: [] };
    }

    onPointerMove(event) {
      const p = this.pointer;
      if (!p || event.pointerId !== p.id) return;
      const dx = event.clientX - p.x;
      const dy = event.clientY - p.y;
      if (!p.moved) {
        if (Math.hypot(dx, dy) < 8) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          this.pointer = null; // senkrecht: die Seite scrollt
          return;
        }
        p.moved = true;
        this.dragging = true;
        this.settle();
        this.update(); // Laufen anhalten, danach steht die belt-Strecke fest
        // Start in die Mitte des Bands legen: Platz nach beiden Seiten
        this.place(this.offset);
        p.offset = this.offset + this.half;
        p.belt = this.beltOffset();
        try {
          this.viewport.setPointerCapture(event.pointerId);
        } catch (error) {
          // schon losgelassen
        }
      }
      p.samples.push({ x: event.clientX, t: event.timeStamp });
      if (p.samples.length > 5) p.samples.shift();

      let value = p.offset - dx;
      const width = this.viewport.clientWidth;
      if (value + p.belt < 0) {
        p.offset += this.half;
        value += this.half;
      } else if (value + p.belt + width > 3 * this.half) {
        p.offset -= this.half;
        value -= this.half;
      }
      this.render(value, null);
    }

    onPointerUp(event) {
      const p = this.pointer;
      if (!p || event.pointerId !== p.id) return;
      this.pointer = null;
      if (!p.moved) return;
      this.dragging = false;
      this.suppressClick = true;
      setTimeout(() => (this.suppressClick = false), 400);
      // Schwung aus den letzten Bewegungen
      const first = p.samples[0];
      const last = p.samples[p.samples.length - 1];
      const velocity = first && last && last.t > first.t ? (last.x - first.x) / (last.t - first.t) : 0; // px/ms
      const throwDistance = Math.max(-900, Math.min(900, -velocity * 280));
      this.hold();
      if (Math.abs(throwDistance) > 4) this.glideBy(throwDistance, 'fling');
      else this.place(this.offset);
    }

    hold() {
      this.holdUntil = performance.now() + RESUME_AFTER;
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => this.update(), RESUME_AFTER + 20);
      this.update();
    }

    update() {
      if (!this.auto) return;
      const run =
        !this.static &&
        !reduceMotion.matches &&
        this.visible &&
        !this.hovered &&
        !this.focused &&
        !this.dragging &&
        !this.userPaused &&
        performance.now() >= this.holdUntil;
      if (run && this.auto.playState !== 'running') {
        // Vor dem Weiterlaufen ins Grundmaß, damit eine volle Reihe Lauf Platz hat
        if (!this.reel.classList.contains('is-gliding') && !this.reel.classList.contains('is-flinging')) {
          this.place(this.offset);
        }
        this.auto.play();
      } else if (!run && this.auto.playState === 'running') {
        this.auto.pause();
      }
    }
  }

  customElements.define('fs-motif-band', FsMotifBand);
})();
